import { z } from 'zod';
import { idempotencyKeySchema } from './common.schemas';
import type { TripEventType } from './trip-event.schemas';

/**
 * What a driver's phone may report. `trip_opened` and `dispatched` are the office's and the platform's to write, so a
 * phone sending one is refused. Typed against TRIP_EVENT_TYPES so a rename there stops this compiling.
 */
export const DRIVER_EVENT_TYPES = [
  'accepted',
  'declined',
  'driver_confirmed',
  'departed',
  'stop_reached',
  'arrived',
  'loaded',
  'unloaded',
  'location_pinged',
  'fuel_stop',
  'exception',
  'pod_captured',
  'handover_acknowledged',
] as const satisfies readonly TripEventType[];
export type DriverEventType = (typeof DRIVER_EVENT_TYPES)[number];

export const MAX_SYNC_EVENTS_PER_BATCH = 100;
export const MAX_SYNC_EVENT_PAYLOAD_BYTES = 8 * 1024;

/**
 * Request schemas here are deliberately not `.strict()`: an app newer than the server sends fields this version
 * does not know, and refusing the whole batch for that would strand its outbox. Compatibility is held by additive-only
 * changes and the frozen fixtures in `sync-contract/`.
 * `type` is a plain string so an unknown one is rejected for that event alone rather than failing the batch.
 */
export const syncEventSchema = z.object({
  /** The event's own idempotency key; also stored as `trip_events.idempotency_key`, so a replay can never double a row. */
  key: idempotencyKeySchema,
  tripId: z.uuid(),
  type: z.string().min(1).max(64),
  /** The phone's clock, with its offset. The server stamps its own time separately. */
  deviceTime: z.iso.datetime({ offset: true }),
  payload: z
    .record(z.string(), z.unknown())
    .default({})
    .refine((payload) => JSON.stringify(payload).length <= MAX_SYNC_EVENT_PAYLOAD_BYTES, { message: 'payload is larger than 8 KB' }),
  location: z.object({ lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180) }).optional(),
});
export type SyncEventInput = z.infer<typeof syncEventSchema>;

/** `POST /sync/events`. */
export const syncEventsRequestSchema = z.object({ events: z.array(syncEventSchema).min(1).max(MAX_SYNC_EVENTS_PER_BATCH) });
export type SyncEventsRequest = z.infer<typeof syncEventsRequestSchema>;

/**
 * Permanent reasons an event is refused; the app drops it rather than retrying. `trip_reassigned` is an event the driver captured after
 * the trip was handed to someone else (gitaumoses4/chukua#80); one captured before it is kept. `invalid_payload` is an event whose payload
 * cannot be right however often it is sent (a `declined` with no reason, gitaumoses4/chukua#70).
 */
export const SYNC_EVENT_REJECTION_REASONS = [
  'unknown_type',
  'trip_not_found',
  'key_reused',
  'trip_reassigned',
  'handover_not_found',
  'invalid_payload',
] as const;
export type SyncEventRejectionReason = (typeof SYNC_EVENT_REJECTION_REASONS)[number];

export const SYNC_EVENT_OUTCOMES = ['accepted', 'duplicate', 'rejected'] as const;

export const syncEventResultSchema = z.object({
  key: z.string(),
  outcome: z.enum(SYNC_EVENT_OUTCOMES),
  /** Present only when `outcome` is `rejected`. */
  reason: z.enum(SYNC_EVENT_REJECTION_REASONS).optional(),
});
export type SyncEventResult = z.infer<typeof syncEventResultSchema>;

/** One result per submitted event, in the order sent. */
export const syncEventsResponseSchema = z.object({ results: z.array(syncEventResultSchema) });
export type SyncEventsResponse = z.infer<typeof syncEventsResponseSchema>;
